import React from 'react';
import styled from 'styled-components';
import { AiFillGithub } from "react-icons/ai";
import { FaExternalLinkAlt } from "react-icons/fa";

// Dark theme card matching existing styles
const Card = styled.div`
  background-color: #000; 
  border: 2px solid #333232;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 400px;
  margin: auto;
  color: white;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 11px 15px rgba(0, 0, 0, 0.3);
    border-color: #555;
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: #000;
  border-bottom: 1px solid #333232;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(1.1);
  transition: transform 0.6s ease-out;

  ${Card}:hover & {
    transform: scale(0.98);
  }
`;

const Content = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const Title = styled.h3`
  margin: 0 0 10px 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: white;
  font-family: 'Oswald', sans-serif;
  letter-spacing: 0.5px;
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

const JobTag = styled.span`
  background-color: #333232;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 25px;
  font-family: 'Quattrocento', serif;
  letter-spacing: 0.5px;
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: #ccc;
  line-height: 1.6;
  margin-bottom: 16px;
  flex-grow: 1;
  font-family: 'Quattrocento', serif;
`;

const ToolsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 20px;
`;

const ToolTag = styled.span`
  background-color: transparent;
  color: #aaa;
  font-size: 0.7rem;
  padding: 3px 8px;
  border-radius: 4px;
  border: 1px solid #444;
  font-family: 'Quattrocento', serif;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 15px;
  border-top: 1px solid #333232;
`;

const LinkButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: white;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s;
  background-color: #000;
  border: 2px solid #333232;
  padding: 6px 16px;
  border-radius: 25px;
  font-family: 'Quattrocento', serif;

  &:hover {
    background-color: #332c2c;
    color: white;
    text-decoration: none;
  }
`;

const ProjectCard = ({ project }) => {
  return (
    <Card>
      <ImageContainer>
        <Image src={project.image} alt={project.title} />
      </ImageContainer>
      <Content>
        <Title>{project.title}</Title>
        
        <TagContainer>
          {project.jobTags.map((tag, index) => (
            <JobTag key={index}>{tag}</JobTag>
          ))}
        </TagContainer>

        <Description>{project.description}</Description>

        <ToolsContainer>
          {project.tools.map((tool, index) => (
            <ToolTag key={index}>{tool}</ToolTag>
          ))}
        </ToolsContainer>

        <Footer>
          {project.links.github && (
            <LinkButton href={project.links.github} target="_blank" rel="noopener noreferrer">
              <AiFillGithub size={18} /> Code
            </LinkButton>
          )}
          {project.links.live && (
            <LinkButton href={project.links.live} target="_blank" rel="noopener noreferrer">
              <FaExternalLinkAlt size={14} /> Live
            </LinkButton>
          )}
        </Footer>
      </Content>
    </Card>
  );
};

export default ProjectCard;
